import { useState } from 'react';
import { ChevronDown, Download } from 'lucide-react';

import { downloadUrl } from '@/api/client';
import { useExpiryBands } from '@/api/hooks/useAnalytics';
import { Can } from '@/auth/guards';
import { PERMISSIONS } from '@/auth/permissions';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { cn, formatDate, money } from '@/lib/utils';

const BAND_TONE: Record<string, string> = {
  band1: 'border-l-status-out',
  band2: 'border-l-status-low',
  band3: 'border-l-status-expiring',
  band4: 'border-l-primary',
};

export function ExpiryBandsTab() {
  const { data, isLoading } = useExpiryBands(90);
  const [openBand, setOpenBand] = useState<string | null>(null);

  if (isLoading || !data) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-20" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">
          Stock expiring within {data.windowDays} days, grouped by cash value at risk.
        </p>
        <Can permission={PERMISSIONS.exportsCreate}>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" asChild>
              <a href={downloadUrl('/api/export/expiry', { days: data.windowDays })}>
                <Download /> XLSX
              </a>
            </Button>
            <Button variant="outline" size="sm" asChild>
              <a href={downloadUrl('/api/export/expiry/csv', { days: data.windowDays })}>
                <Download /> CSV
              </a>
            </Button>
            <Button variant="outline" size="sm" asChild>
              <a href={downloadUrl('/api/export/expiry/pdf', { days: data.windowDays })}>
                <Download /> PDF
              </a>
            </Button>
          </div>
        </Can>
      </div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Card className="p-4">
          <p className="tabular text-2xl font-bold">{data.grandTotal.count}</p>
          <p className="text-xs text-muted-foreground">medicines at risk</p>
        </Card>
        <Card className="p-4">
          <p className="tabular text-2xl font-bold">{data.grandTotal.totalUnits}</p>
          <p className="text-xs text-muted-foreground">total units</p>
        </Card>
        <Card className="col-span-2 p-4">
          <p className="tabular text-2xl font-bold text-status-expired">
            {money(data.grandTotal.totalRetailValue)}
          </p>
          <p className="text-xs text-muted-foreground">retail value at risk</p>
        </Card>
      </div>

      <div className="space-y-2">
        {data.bands.map((band) => {
          const expanded = openBand === band.key;
          return (
            <Card key={band.key} className={cn('border-l-4', BAND_TONE[band.key])}>
              <button
                type="button"
                onClick={() => setOpenBand(expanded ? null : band.key)}
                className="flex w-full items-center justify-between gap-3 p-4 text-left"
              >
                <div>
                  <p className="text-sm font-medium">{band.label}</p>
                  <p className="text-xs text-muted-foreground">
                    {band.count} medicine(s) · {band.totalUnits} unit(s)
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <span className="tabular text-lg font-semibold">{money(band.totalRetailValue)}</span>
                  <ChevronDown className={cn('h-4 w-4 shrink-0 transition-transform', expanded && 'rotate-180')} />
                </div>
              </button>

              {expanded && (
                <CardContent className="border-t border-border pt-3">
                  {band.items.length === 0 ? (
                    <p className="py-4 text-center text-sm text-muted-foreground">Nothing in this band.</p>
                  ) : (
                    <div className="space-y-1.5">
                      {band.items.map((item) => (
                        <div key={item.id} className="flex items-center gap-3 text-sm">
                          <div className="min-w-0 flex-1">
                            <p className="truncate">{item.name}</p>
                            <p className="text-xs text-muted-foreground">
                              {item.stock} {item.unit} · Batch {item.batchNo ?? '—'}
                            </p>
                          </div>
                          <Badge variant={item.daysToExpiry < 0 ? 'expired' : 'expiring'} className="shrink-0">
                            {item.daysToExpiry < 0 ? 'Expired' : `${item.daysToExpiry}d`}
                          </Badge>
                          <span className="tabular w-20 shrink-0 text-right font-medium">
                            {money(item.retailValue)}
                          </span>
                        </div>
                      ))}
                      {band.truncated && (
                        <p className="pt-2 text-center text-xs text-muted-foreground">
                          Showing the top 50, soonest expiry first.
                        </p>
                      )}
                    </div>
                  )}
                </CardContent>
              )}
            </Card>
          );
        })}
      </div>

      <p className="text-center text-xs text-muted-foreground">
        As of {formatDate(data.cutoffDate)}
      </p>
    </div>
  );
}